import React from 'react'
import { FaFileDownload, FaFilter, FaSearch } from 'react-icons/fa'
import { Badge, Button, Tooltip } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { PositionEnum } from 'types/cssTypes'

/**
 * Props for FilterHeader component
 */
type FilterHeaderProps = {
  filterCount: number
  totalItems: number
  onSearch: () => void
  onClear: () => void
  onDownload: () => void
}

/**
 * FilterHeader Component
 * Displays filter title, active filter count, and action buttons
 */
export const FilterHeader: React.FC<FilterHeaderProps> = ({
  filterCount,
  totalItems,
  onSearch,
  onClear,
  onDownload,
}) => {
  const filtersTextPlural = filterCount > 1 ? 's' : ''

  return (
    <Header>
      <HeaderLeft>
        <FaFilter /> Claim Filters
      </HeaderLeft>
      <HeaderSpacer />
      <HeaderActions>
        {/* Active Filters Badge */}
        {filterCount > 0 && (
          <Badge variant="tag" fill="dark" type="grey">
            {filterCount} Filter{filtersTextPlural} Applied
          </Badge>
        )}

        {/* Search Button */}
        <Button variant="primary" size="small" radius="rounded" onClick={onSearch}>
          <FaSearch /> Search
        </Button>

        {/* Clear Button (only shown when filters are active) */}
        {filterCount > 0 && (
          <Button variant="primary" size="small" radius="rounded" onClick={onClear}>
            <Tooltip tooltipText="Clear all filters" position={PositionEnum.top}>
              <>Clear All</>
            </Tooltip>
          </Button>
        )}

        {/* Download Button */}
        <Button variant="secondary" size="small" radius="rounded" disabled={totalItems === 0} onClick={onDownload}>
          <Tooltip tooltipText="Download CSV" position={PositionEnum.top}>
            <>
              Download <FaFileDownload />
            </>
          </Tooltip>
        </Button>
      </HeaderActions>
    </Header>
  )
}

/* ---------- Styled Components ---------- */

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  height: 32px;
  ${({ theme }) => theme.typography.s.medium};
  margin-bottom: ${({ theme }) => theme.spacing.s};
`

const HeaderLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxxs};
`

const HeaderSpacer = styled.div`
  flex: 1;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
`
